"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useHVACData = void 0;
const react_1 = require("react");
const useHVACUsageData_1 = require("./useHVACUsageData");
const useHVACData = (sensor) => {
    const { data: hvacData, loading, error: hvacError, } = (0, useHVACUsageData_1.useHVACUsageData)({
        sensorId: sensor?.id,
        days: 7,
        enabled: !!sensor?.id,
    });
    const lastReported = (0, react_1.useMemo)(() => {
        if (!hvacData || hvacData.length === 0) {
            return undefined;
        }
        const dataWithTimestamps = hvacData.filter((data) => data.timestamp);
        if (dataWithTimestamps.length === 0) {
            return undefined;
        }
        const sortedData = dataWithTimestamps.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        const mostRecentData = sortedData[0];
        if (!mostRecentData.timestamp) {
            return undefined;
        }
        return new Date(mostRecentData.timestamp * 1000).toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    }, [hvacData]);
    const { temperatureData, humidityData, currentTemperature, currentHumidity, currentPressure } = (0, react_1.useMemo)(() => {
        if (!hvacData || hvacData.length === 0) {
            return {
                temperatureData: [],
                humidityData: [],
                currentTemperature: undefined,
                currentHumidity: undefined,
                currentPressure: undefined,
            };
        }
        const temperatureData = [];
        const humidityData = [];
        // Get today's date in YYYY-MM-DD format to match the data format
        const today = new Date();
        const todayString = today.getFullYear() + '-' +
            String(today.getMonth() + 1).padStart(2, '0') + '-' +
            String(today.getDate()).padStart(2, '0');
        hvacData
            .sort((a, b) => new Date(a.label).getTime() - new Date(b.label).getTime())
            .forEach((dailyData) => {
            // Parse the date using consistent logic
            const date = new Date(dailyData.label + 'T00:00:00');
            // Determine if this is today's data
            const isToday = dailyData.label === todayString;
            // Get day of week - ensure today is labeled as "Tue" not "Mon"
            let label = date.toLocaleDateString('en-US', { weekday: 'short' });
            // Force correct label for today's data
            if (isToday) {
                label = today.toLocaleDateString('en-US', { weekday: 'short' });
                console.log(`Corrected today's label to: ${label}`);
            }
            if (dailyData.temperature !== undefined) {
                temperatureData.push({
                    value: dailyData.temperature ? dailyData.temperature * 1.8 + 32 : -999,
                    label,
                });
            }
            else {
                temperatureData.push({
                    value: null,
                    label,
                });
            }
            if (dailyData.humidity !== undefined) {
                humidityData.push({
                    value: dailyData.humidity ?? -999,
                    label,
                });
            }
            else {
                humidityData.push({
                    value: null,
                    label,
                });
            }
        });
        // Make sure we have the most recent data by sorting
        const sortedByDate = [...hvacData].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        // Find the most recent data that has both temperature and humidity values
        const mostRecentValidData = sortedByDate.find(data => data.temperature !== null && data.temperature !== undefined &&
            data.humidity !== null && data.humidity !== undefined);
        // Fall back to the most recent data if no valid entries are found
        const mostRecentData = mostRecentValidData || sortedByDate[0] || hvacData[hvacData.length - 1];
        return {
            temperatureData,
            humidityData,
            currentTemperature: mostRecentData?.temperature
                ? mostRecentData.temperature * 1.8 + 32
                : undefined,
            currentHumidity: mostRecentData?.humidity,
            currentPressure: mostRecentData?.pressure,
        };
    }, [hvacData]);
    return {
        temperatureData,
        humidityData,
        currentTemperature,
        currentHumidity,
        currentPressure,
        lastReported,
        loading,
        error: hvacError ? new Error(hvacError) : null,
    };
};
exports.useHVACData = useHVACData;
